import { api } from '../services/api';
import { cctvEndpoints, poolEndpoints } from '../api/endpoints';
import { CctvCreate, CctvUpdate, Cctv } from '../types/cctv';

interface PoolResponse {
  id: number;
  code: string;
  name: string;
}

// 시설 코드로 ID 조회
const getPoolIdByCode = async (code: string): Promise<PoolResponse> => {
  try {
    console.log(`[standardCctvApi] 시설 코드 ${code}의 ID 조회 시작`);
    const response = await api.get(poolEndpoints.getByCode(code));
    console.log(`[standardCctvApi] 시설 ID 조회 응답:`, response.data);
    return response.data;
  } catch (error) {
    console.error(`[standardCctvApi] 시설 코드 ${code}의 ID 조회 실패:`, error);
    throw error;
  }
};

// CCTV 목록 조회
const getCctvsByPoolId = async (poolId: number) => {
  try {
    console.log(`[standardCctvApi] 시설 ID ${poolId}의 CCTV 목록 조회 시작`);
    const response = await api.get(cctvEndpoints.getAll(poolId));
    console.log(`[standardCctvApi] CCTV 목록 조회 응답:`, response.data);
    return response.data;
  } catch (error) {
    console.error(`[standardCctvApi] CCTV 목록 조회 실패:`, error);
    throw error;
  }
};

// 단일 CCTV 조회
const getCctv = async (poolId: number, cctvId: number) => {
  try {
    console.log(`[standardCctvApi] CCTV 조회 시작: Pool ID ${poolId}, CCTV ID ${cctvId}`);
    const response = await api.get(cctvEndpoints.getById(poolId, cctvId));
    console.log(`[standardCctvApi] CCTV 조회 응답:`, response.data);
    
    if (response.status === 200) {
      return {
        success: true,
        data: response.data,
        message: 'CCTV 정보 수신',
        status: response.status
      };
    }
    
    return {
      success: false,
      data: null,
      message: 'CCTV 정보를 가져오기 실패',
      status: response.status
    };
  } catch (error) {
    console.error(`[standardCctvApi] CCTV 조회 실패:`, error);
    return {
      success: false,
      data: null,
      message: error.response?.data?.detail || error.message || 'CCTV 정보를 가져오기 실패',
      error: error,
      status: error.response?.status || 500
    };
  }
};

// CCTV 생성
const createCctv = async (poolId: number, data: CctvCreate) => {
  try {
    console.log(`[standardCctvApi] 시설 ID ${poolId}에 CCTV 생성 시작`, data);
    const response = await api.post(cctvEndpoints.create(poolId), {
      ...data,
      pool_id: poolId,
      status: data.status?.toUpperCase() || 'ACTIVE'
    });
    console.log(`[standardCctvApi] CCTV 생성 응답:`, response.data);
    
    if (response.status === 200 || response.status === 201) {
      return {
        success: true,
        data: response.data,
        message: 'CCTV가 생성됨',
        status: response.status
      };
    }
    
    return {
      success: false,
      data: null,
      message: 'CCTV 생성에 실패',
      status: response.status
    };
  } catch (error) {
    console.error(`[standardCctvApi] CCTV 생성 실패:`, error);
    return {
      success: false,
      data: null,
      message: error.response?.data?.detail || error.message || 'CCTV 생성 중 오류 발생',
      error: error,
      status: error.response?.status || 500
    };
  }
};

// CCTV 수정
const updateCctv = async (poolId: number, cctvId: number, data: CctvUpdate) => {
  try {
    console.log(`[standardCctvApi] CCTV 수정 시작: Pool ID ${poolId}, CCTV ID ${cctvId}`, data);
    const response = await api.put(cctvEndpoints.update(poolId, cctvId), {
      ...data,
      status: data.status?.toUpperCase() || 'ACTIVE'
    });
    console.log(`[standardCctvApi] CCTV 수정 응답:`, response.data);
    
    if (response.status === 200) {
      return {
        success: true,
        data: response.data,
        message: 'CCTV가 수정됨',
        status: response.status
      };
    }
    
    return {
      success: false,
      data: null,
      message: 'CCTV 수정에 실패',
      status: response.status
    };
  } catch (error) {
    console.error(`[standardCctvApi] CCTV 수정 실패:`, error);
    return {
      success: false,
      data: null,
      message: error.response?.data?.detail || error.message || 'CCTV 수정 중 오류 발생',
      error: error,
      status: error.response?.status || 500
    };
  }
};

// CCTV 삭제
const deleteCctv = async (poolId: number, cctvId: number) => {
  try {
    console.log(`[standardCctvApi] CCTV 삭제 시작: Pool ID ${poolId}, CCTV ID ${cctvId}`);
    const response = await api.delete(cctvEndpoints.delete(poolId, cctvId));
    console.log(`[standardCctvApi] CCTV 삭제 응답:`, response.data);
    // 백엔드가 {message}만 주거나 204 빈 본문이어도 UI가 success로 처리하도록 정규화
    const data = response?.data;
    if (data && typeof data === 'object') {
      return {
        success: data.success !== false,
        message: data.message || 'CCTV가 삭제되었습니다',
        ...data,
      };
    }
    return { success: true, message: 'CCTV가 삭제되었습니다', data };
  } catch (error) {
    console.error(`[standardCctvApi] CCTV 삭제 실패:`, error);
    throw error;
  }
};

// 시설 코드로 CCTV 목록 조회
const getCctvsByPoolCode = async (poolCode: string) => {
  try {
    console.log(`[standardCctvApi] 시설 코드 ${poolCode}의 CCTV 목록 조회 시작`);
    const response = await api.get(`/api/pools/code/${poolCode}/cctvs`);
    console.log(`[standardCctvApi] CCTV 목록 조회 응답:`, response.data);
    return response.data;
  } catch (error) {
    console.error(`[standardCctvApi] CCTV 목록 조회 실패:`, error);
    throw error;
  }
};

export const standardCctvApi = {
  getPoolIdByCode,
  getCctvsByPoolId,
  getCctv,
  createCctv,
  updateCctv,
  deleteCctv,
  getCctvsByPoolCode
}; 